import {
  faCheck,
  faCheckCircle,
  faChevronRight,
  faCircle,
  faClock,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Pill } from '../Text'
import { Button } from '../Button'
import { Checkbox } from '../Form/Checkbox/Checkbox'

/**
 * variant decides how the item's children are rendered when it is selected:
 *  - 'modal'     → children render inside a Modal
 *  - 'accordion' → children render beneath the item
 *  - 'slider'    → children replace the list with a slide animation
 *  - 'panel'     → children render in a side panel
 *  - 'list'      → children render inline below the full checklist
 *  - 'button'    → item is just a button; children are ignored and onClick handles navigation
 */
export type TodoListItemVariant =
  | 'modal'
  | 'accordion'
  | 'slider'
  | 'panel'
  | 'list'
  | 'button'

export type ChecklistItemStatus = 'Pending' | 'Incomplete' | 'Completed'

export type TodoProps = {
  title: string
  subtitle?: string
  status?: ChecklistItemStatus
  disabled?: boolean
  optional?: boolean
  hidden?: boolean
  hasPermissions?: boolean
  step?: number
  variant?: TodoListItemVariant
  children?: React.ReactNode
  onClick?: () => void
  onClose?: () => void
  className?: string
  rightSlot?: React.ReactNode
  isActive?: boolean
  /** When true, a completed Todo's title is rendered with a
   * strike-through. Defaults to false — most of this app's Todos
   * represent always-editable sections (e.g. "Add Passes") where a
   * strike-through reads as "done forever" instead of "done for now".
   * Opt in only when the Todo is literally a one-shot task that won't
   * need revisiting. */
  strikeWhenComplete?: boolean
  /** Optional percent-complete indicator from 0-100. When provided, replaces
   * the circle/clock/check status icon with a circular progress ring whose
   * center shows the percentage. Useful for Todos that represent partially
   * finished work ("4 of 10 tasks assigned"). */
  percentComplete?: number
  /** Hide the "Step 1 / Step 2 / todo" prefix on this Todo. Normally
   * controlled by `<TodoList hideStepText />` which fans the flag out
   * to every child, but can also be set on an individual Todo. */
  hideStepText?: boolean
  /** When true, render a small circular checkbox on the left of the
   * Todo. Clicking the checkbox manually marks the Todo as Completed
   * regardless of `percentComplete`. Clicking it again reverts the
   * Todo to whatever status/percentComplete the caller passed. When
   * `status === 'Completed'` (the source-of-truth prop) the checkbox
   * is effectively disabled — the user can't un-complete an already-
   * completed item via the in-Todo override. */
  checkbox?: boolean
  /** Fires with the new effective "is completed" boolean whenever the
   * user clicks the checkbox. Only meaningful when `checkbox` is true. */
  onClickCheckbox?: (checked: boolean) => void
}

const getIcon = (status?: ChecklistItemStatus) => {
  if (status === 'Pending') return faClock
  if (status === 'Completed') return faCheckCircle
  if (status === 'Incomplete') return faWarning
  return faCircle
}

const getIconColor = (status?: ChecklistItemStatus) => {
  if (status === 'Pending') return 'text-blue-400'
  if (status === 'Completed') return 'text-green-500'
  if (status === 'Incomplete') return 'text-yellow-400'
  return 'text-gray-400'
}

export const Todo = ({ variant = 'button', ...props }: TodoProps) => {
  // Internal override for "user manually marked this Todo complete via
  // the checkbox". When undefined, the displayed status falls back to
  // the caller-provided props. When true, the displayed status is
  // forced to 'Completed'. We never store `false` — the spec is "click
  // once to complete, click again to revert to default", so the inverse
  // state is always "use the default".
  const [manuallyCompleted, setManuallyCompleted] = useState<boolean>(false)

  if (props.hidden) return null

  // When `percentComplete` is handed in, it drives the status so the
  // caller can run the checklist with a single "how many subtasks done"
  // number instead of also managing the `status` prop:
  //   100   → Completed
  //   1-99  → Incomplete (in progress)
  //   0     → Pending    (not started)
  const statusFromPercent: ChecklistItemStatus | undefined =
    typeof props.percentComplete === 'number'
      ? props.percentComplete === 100
        ? 'Completed'
        : props.percentComplete > 0
        ? 'Incomplete'
        : 'Pending'
      : undefined
  const defaultStatus: ChecklistItemStatus | undefined =
    statusFromPercent ?? props.status
  const status: ChecklistItemStatus | undefined = manuallyCompleted
    ? 'Completed'
    : defaultStatus
  const resolvedProps = { ...props, status }

  // The checkbox is locked out when the source-of-truth `status` prop
  // is 'Completed' — you can't manually un-complete something the
  // caller already says is done.
  const checkboxLocked = props.status === 'Completed'
  const checkboxChecked = status === 'Completed'

  const onCheckboxClick = () => {
    if (checkboxLocked) return
    // If we're currently showing Completed, revert to default. Otherwise
    // force-complete via the internal override.
    const next = !checkboxChecked
    setManuallyCompleted(next ? true : false)
    // Effective displayed state after the click. When reverting, the
    // derived default may still resolve to Completed (e.g. percent=100),
    // but from the caller's perspective the user clicked "off" — we
    // hand back the literal click target boolean, not the resolved one,
    // so the caller can implement their own "force revert" semantics.
    props.onClickCheckbox?.(next)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (props.disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      props.onClick?.()
    }
  }

  return (
    <div
      role='button'
      aria-disabled={props.disabled}
      tabIndex={props.disabled ? -1 : 0}
      data-status={status}
      data-variant={variant}
      data-testid={`todo-item-${props.step ?? ''}`}
      onClick={props.disabled ? undefined : props.onClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        `group w-full text-left flex items-center gap-4 rounded-2xl border cursor-pointer`,
        `border-slate-200 bg-white p-4 transition`,
        `hover:shadow-sm focus-visible:outline-none focus-visible:ring-2`,
        `focus-visible:ring-blue-500`,
        props.disabled && 'opacity-40 cursor-not-allowed hover:shadow-none',
        props.isActive &&
          variant === 'accordion' &&
          'rounded-b-none border-t-0 border-r-0 border-l-0 border-slate-400',
        props.className,
      )}>
      {/* Optional manual-completion checkbox. Rendered to the far left
       * so it sits outside the existing status-icon slot (which might
       * still show the percentComplete ring or the Completed icon).
       * Click is intercepted via stopPropagation inside the Checkbox
       * itself so tapping it doesn't also open the accordion. */}
      {props.checkbox && (
        <Checkbox
          variant='circle'
          checked={checkboxChecked}
          disabled={checkboxLocked || props.disabled}
          noLabel
          ariaLabel={`Mark ${props.title} as complete`}
          onChange={onCheckboxClick}
        />
      )}

      {/* Leading icon slot — only rendered when we have something to
       * show (percentComplete ring or Completed status icon). When the
       * Todo is pending and has no percentComplete, skip the slot so
       * there's no empty space between the left edge and the "Step x"
       * text. */}
      {status !== 'Completed' && typeof props.percentComplete === 'number' && (
        <div className='shrink-0 w-6 h-6 flex items-center justify-center'>
          <PercentRing percent={props.percentComplete} />
        </div>
      )}
      {status === 'Completed' && (
        <div className='shrink-0 w-6 h-6 flex items-center justify-center'>
          <FontAwesomeIcon
            icon={getIcon(status)}
            className={`text-xl ${getIconColor(status)}`}
            aria-hidden
          />
        </div>
      )}

      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2'>
          <StepText {...resolvedProps} />
          <StepTitle {...resolvedProps} />
          <StepPillArea variant={variant} {...resolvedProps} />
        </div>
      </div>

      <div className='flex items-center gap-2 shrink-0 text-slate-400 group-hover:text-slate-600'>
        {props.rightSlot}
        <FontAwesomeIcon
          icon={faChevronRight}
          className='text-sm'
          aria-hidden
        />
      </div>
    </div>
  )
}

const StepTitle = (
  props: Pick<
    TodoProps,
    'title' | 'status' | 'variant' | 'optional' | 'strikeWhenComplete'
  >,
) => {
  return (
    <React.Fragment>
      <p
        className={twMerge(
          'font-medium text-slate-900 truncate',
          props.status === 'Completed' &&
            props.strikeWhenComplete &&
            'line-through',
        )}>
        {props.title}
      </p>
      {props.optional && <p className='text-xs text-gray-500'>(optional)</p>}
      <span className='sr-only'>—</span>
    </React.Fragment>
  )
}

const StepPillArea = ({
  status,
  ...props
}: Pick<
  TodoProps,
  'status' | 'isActive' | 'variant' | 'subtitle' | 'onClose'
>) => {
  if (props.isActive && props.variant === 'accordion')
    return (
      <Button
        type='button'
        variant='link-warn'
        data-testid='todo-close'
        onClick={(event) => {
          event.stopPropagation()
          props.onClose?.()
        }}>
        close
      </Button>
    )

  return (
    <div className='opacity-50'>
      {status === 'Completed' && (
        <p className='text-xs py-0 text-blue-400 font-semibold'>Modify</p>
      )}
      {status === 'Pending' && <Pill>Not Started</Pill>}
      {status === 'Incomplete' && <Pill color='yellow'>In Progress</Pill>}
      {props.subtitle ? (
        <p className='text-sm text-slate-600 mt-1 line-clamp-2'>
          {props.subtitle}
        </p>
      ) : null}
    </div>
  )
}

const StepText = (
  props: Pick<
    TodoProps,
    'step' | 'status' | 'variant' | 'isActive' | 'hideStepText'
  >,
) => {
  if (props.hideStepText) return null
  if (props.status === 'Completed') return null

  return <p className='text-xs pr-3'>Step {props.step}</p>
}

/** Small circular progress ring — stroked SVG circle with the percentage
 * rendered in the center. Sized to fit in the 24×24 leading icon slot so
 * a Todo with percentComplete has the same height as one without.
 * At 100% the percentage number is replaced with a green checkmark
 * inside the (now fully stroked) ring. */
const PercentRing = ({ percent }: { percent: number }) => {
  const clamped = Math.max(0, Math.min(100, percent))
  const size = 24
  const stroke = 2.5
  const radius = size / 2 - stroke / 2
  const circumference = radius * 2 * Math.PI
  const dashOffset = circumference - (clamped / 100) * circumference
  const strokeColor = '#3b82f6'

  return (
    <div
      className='relative inline-flex items-center justify-center'
      style={{ width: size, height: size }}>
      <svg width={size} height={size} className='block' aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='transparent'
          stroke='#e5e7eb'
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='transparent'
          stroke={strokeColor}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <span className='absolute text-[8px] font-semibold text-slate-700 leading-none'>
        {Math.round(clamped)}
      </span>
    </div>
  )
}
